/**
 * Filter condition enums for consistent string handling across the application
 */

/**
 * Filter conditions for single values
 */
export enum FilterCondition {
  IS = "is",
  IS_NOT = "is not",
}

/**
 * Filter conditions for multiple values (consolidated)
 */
export enum FilterConditionConsolidated {
  IS_ANY_OF = "is any of",
  IS_NOT = "is not",
}

/**
 * Combined enum for all filter conditions
 */
export enum FilterConditionAll {
  IS = "is",
  IS_NOT = "is not",
  IS_ANY_OF = "is any of",
}

/**
 * Filter types for internal use
 */
export enum FilterType {
  ASSIGNEE = "assignee",
  STATUS = "status",
  PRIORITY = "priority",
  ESTIMATE = "estimate",
  LABEL = "label",
  DUE_DATE = "dueDate",
  SEARCH = "search",
}

/**
 * Filter keys for FilterState object
 */
export enum FilterKey {
  USER_IDS = "userIds",
  LABELS = "labels",
  STATUSES = "statuses",
  ESTIMATES = "estimates",
  PRIORITIES = "priorities",
  DUE_DATES = "dueDates",
  SEARCH = "search",
}

/**
 * Filter display names for UI
 */
export enum FilterDisplayName {
  ASSIGNEE = "Assignee",
  STATUS = "Status",
  PRIORITY = "Priority",
  ESTIMATE = "Estimate",
  LABEL = "Label",
  DUE_DATE = "Due date",
  SEARCH = "Search",
}

/**
 * Due date filter values
 */
export enum DueDateFilterValue {
  NONE = "none",
  OVERDUE = "overdue",
  TODAY = "today",
  THIS_WEEK = "thisWeek",
  NEXT_WEEK = "nextWeek",
  LATER = "later",
}

/**
 * View types
 */
export enum ViewType {
  GRID = "grid",
  LIST = "list",
}

/**
 * Default filter condition for single values
 */
export const DEFAULT_FILTER_CONDITION = FilterCondition.IS;

/**
 * Default filter condition for consolidated values
 */
export const DEFAULT_CONSOLIDATED_CONDITION =
  FilterConditionConsolidated.IS_ANY_OF;

/**
 * Get available conditions for single values
 */
export const getSingleValueConditions = (): FilterCondition[] => [
  FilterCondition.IS,
  FilterCondition.IS_NOT,
];

/**
 * Get available conditions for multiple values
 */
export const getMultipleValueConditions = (): FilterConditionConsolidated[] => [
  FilterConditionConsolidated.IS_ANY_OF,
  FilterConditionConsolidated.IS_NOT,
];

/**
 * Get all available conditions
 */
export const getAllConditions = (): FilterConditionAll[] => [
  FilterConditionAll.IS,
  FilterConditionAll.IS_NOT,
  FilterConditionAll.IS_ANY_OF,
];

/**
 * Check if a condition is valid for single values
 */
export const isSingleValueCondition = (
  condition: string,
): condition is FilterCondition => {
  return Object.values(FilterCondition).includes(condition as FilterCondition);
};

/**
 * Check if a condition is valid for multiple values
 */
export const isMultipleValueCondition = (
  condition: string,
): condition is FilterConditionConsolidated => {
  return Object.values(FilterConditionConsolidated).includes(
    condition as FilterConditionConsolidated,
  );
};

/**
 * Get smart condition based on values count and current condition
 */
export const getSmartCondition = (
  values: (string | number)[] | undefined,
  currentCondition?: string,
): string => {
  if (!values || values.length === 0) return FilterCondition.IS;

  if (values.length === 1) {
    return currentCondition === FilterCondition.IS_NOT
      ? FilterCondition.IS_NOT
      : FilterCondition.IS;
  } else {
    return currentCondition === FilterCondition.IS_NOT
      ? FilterCondition.IS_NOT
      : FilterConditionConsolidated.IS_ANY_OF;
  }
};

/**
 * Get available conditions based on values count
 */
export const getAvailableConditions = (
  values: (string | number)[] | undefined,
): string[] => {
  if (!values || values.length === 0) return getSingleValueConditions();

  if (values.length === 1) {
    return getSingleValueConditions();
  } else {
    return getMultipleValueConditions();
  }
};

/**
 * Utility type for filter conditions
 * Maps all filter keys to FilterConditionAll
 */
export type FilterConditions = Record<FilterKey, FilterConditionAll>;
